import { clsx, type ClassValue } from 'clsx';
import { readableInk } from '../color';
import { Plus, type LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';
import type { Tone } from '../types';

/** Same helper both apps keep in `lib/utils`, and `@codezo/quote-detail` in
 * its own primitives. Duplicated rather than imported — a package can't reach
 * into an app, and depending on the quote package for two lines would tie the
 * brand page's build to the offer engine. */
export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs));
}

/**
 * The small marks the brand page is built from.
 *
 * These deliberately mirror `@codezo/quote-detail/ui/primitives` rather than
 * importing it. They are the same handful of visual pieces — card frame,
 * section heading, badge — and if a third page ever needs them the answer is to
 * lift them into their own package, not to make brand depend on quote.
 *
 * No Button lives here, for the reason the quote package states: every action
 * on this page is a ReactNode slot the app fills, because the app owns
 * permissions, loading state, and what each control opens.
 */

/* ────────────────────────── surfaces ────────────────────────── */

export function Card({
	children,
	className,
	padded,
}: {
	children: ReactNode;
	/** Adds the standard gutter. Cards that own their own rows pass nothing. */
	padded?: boolean;
	className?: string;
}) {
	return (
		<div
			className={cn(
				'overflow-hidden rounded-[var(--r-3)] border border-line-1 bg-surface',
				padded && 'p-5',
				className
			)}
		>
			{children}
		</div>
	);
}

/** The uppercase label used for column heads and small captions throughout. */
export function MicroLabel({ children, className }: { children: ReactNode; className?: string }) {
	return (
		<span
			className={cn(
				'text-2xs font-medium uppercase tracking-[0.08em] text-text-4',
				className
			)}
		>
			{children}
		</span>
	);
}

/** A section's heading row: title, an optional count/hint, and the app's own
 * action on the right. */
export function SectionHead({
	title,
	meta,
	action,
}: {
	title: string;
	meta?: ReactNode;
	action?: ReactNode;
}) {
	return (
		<div className="mb-4 flex flex-wrap items-center justify-between gap-3">
			<div className="flex min-w-0 items-baseline gap-3">
				<h2 className="text-base font-medium text-text-1">{title}</h2>
				{meta && <span className="truncate text-xs text-text-4">{meta}</span>}
			</div>
			{action && <div className="flex shrink-0 items-center gap-2">{action}</div>}
		</div>
	);
}

/* ────────────────────────── marks ────────────────────────── */

const TONE_CLASS: Record<Tone, string> = {
	neutral: 'bg-status-neutral-soft text-text-2',
	info: 'bg-status-info-soft text-status-info',
	success: 'bg-status-success-soft text-status-success',
	warn: 'bg-status-warn-soft text-status-warn',
	danger: 'bg-status-danger-soft text-status-danger',
};

export function Badge({
	tone = 'neutral',
	children,
	className,
}: {
	tone?: Tone;
	children: ReactNode;
	className?: string;
}) {
	return (
		<span
			className={cn(
				'inline-flex items-center gap-1.5 rounded-[var(--r-1)] px-1.5 py-0.5 text-2xs font-medium',
				TONE_CLASS[tone],
				className
			)}
		>
			{children}
		</span>
	);
}

/** The rounded word-pill used for traits, values and usage hints. */
export function Chip({ children, className }: { children: ReactNode; className?: string }) {
	return (
		<span
			className={cn(
				'inline-flex items-center rounded-[var(--r-full)] border border-line-1 bg-surface-fill px-3 py-1 text-xs text-text-2',
				className
			)}
		>
			{children}
		</span>
	);
}

/**
 * The dashed "add one" tile that closes a grid.
 *
 * It sits in the grid rather than beside it so the affordance is where the
 * user's eye already is — after the last item — and so an empty section still
 * shows its shape instead of collapsing to a heading and a button.
 */
export function AddTile({
	label,
	onClick,
	className,
}: {
	label: string;
	onClick?: () => void;
	className?: string;
}) {
	return (
		<button
			type="button"
			onClick={onClick}
			disabled={!onClick}
			className={cn(
				'flex min-h-[7rem] w-full flex-col items-center justify-center gap-2 rounded-[var(--r-3)] border border-dashed border-line-2 bg-transparent text-xs text-text-3 transition-colors duration-[var(--dur-fast)]',
				onClick ? 'hover:border-line-3 hover:text-text-1' : 'cursor-default opacity-50',
				className
			)}
		>
			<Plus size={15} />
			{label}
		</button>
	);
}

/** The centred "nothing here yet" panel a section shows in place of its grid. */
export function EmptyState({
	icon: Icon,
	title,
	hint,
	action,
}: {
	icon: LucideIcon;
	title: string;
	hint?: string;
	action?: ReactNode;
}) {
	return (
		<Card className="py-12">
			<div className="flex flex-col items-center gap-3 text-center">
				<div className="flex h-11 w-11 items-center justify-center rounded-full border border-line-1 bg-surface-fill text-text-3">
					<Icon size={20} strokeWidth={1.5} />
				</div>
				<div className="text-sm text-text-2">{title}</div>
				{hint && <div className="max-w-xs text-xs text-text-4">{hint}</div>}
				{action && <div className="mt-1">{action}</div>}
			</div>
		</Card>
	);
}

/* ────────────────────────── file helpers ────────────────────────── */

/** "SVG · 24 KB". Same shape as the quote package's, for the same reason: the
 * extension is what a designer recognises, the mime type is the fallback. */
export function fileMeta(name: string, mimeType: string, size: number) {
	return `${formatLabel(name, mimeType)} · ${formatBytes(size)}`;
}

export function formatLabel(name: string, mimeType: string) {
	const ext = /\.([a-z0-9]{1,5})$/i.exec(name)?.[1];
	return (ext ?? mimeType.split('/')[1] ?? mimeType).toUpperCase();
}

export function formatBytes(size: number) {
	return size < 1024 * 1024
		? `${(size / 1024).toFixed(0)} KB`
		: `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

/** The brand's initial on its own color — the stand-in wherever a brand has no
 * logo file to show, so a hero or a switcher never renders an empty box. */
export function BrandMark({
	name,
	hex,
	src,
	size = 72,
	className,
}: {
	name: string;
	/** The brand's primary color. Falls back to the app's accent. */
	hex?: string | null;
	/** A rendered logo thumbnail, when one exists. */
	src?: string | null;
	size?: number;
	className?: string;
}) {
	if (src) {
		return (
			<div
				className={cn(
					'bg-stripes flex items-center justify-center overflow-hidden rounded-[var(--r-4)] border border-line-1 p-2',
					className
				)}
				style={{ width: size, height: size }}
			>
				<img src={src} alt={name} className="max-h-full max-w-full object-contain" />
			</div>
		);
	}
	/* The ink is measured off the fill rather than assumed: a pale brand colour
	   wearing hardcoded white lost its initial entirely. `--accent` is a token
	   with no hex to measure, so it takes its own paired foreground. */
	const fill = hex ?? 'var(--accent)';
	const ink = hex ? readableInk(hex) : 'var(--accent-fg)';

	return (
		<div
			className={cn(
				'flex items-center justify-center rounded-[var(--r-4)] border border-line-1 font-medium',
				className
			)}
			style={{
				width: size,
				height: size,
				background: fill,
				color: ink,
				fontSize: size * 0.42,
			}}
		>
			{name.trim().charAt(0).toUpperCase() || '—'}
		</div>
	);
}
